// frontend/src/engine/app/offsite-config.ts
import type { ComponentType } from "react";

/** Props, die jede Slide von der Engine bekommt. (übernommen aus slides/index.ts) */
export interface SlideProps {
  index: number;
  total: number;
  onJump?: (slideNumber: number) => void;
}

/** Ein Schnitt-Marker auf der Presenter-Progress-Bar. */
export interface ProgressCut {
  /** 1-basierte Foliennummer, an der der Marker sitzt. */
  atSlide: number;
  label: string;
  icon: string;
}

/** Bekannte Workshop-Phasen (Reihenfolge ist offsite-spezifisch). */
export type PhaseId =
  | "intro"
  | "topics"
  | "voting"
  | "grouping"
  | "working"
  | "evaluating"
  | "showcase"
  | "evaluated"
  | "overnight"
  | "reveal"
  | "einfuehrung"
  | "abschluss";

/**
 * Der Stations-Ablauf des Strategietags (Briefing Prompt-Teil): acht Schritte
 * mit servergeführten Timern, Rahmenfolien (E1/E2, V1–V3, A1) und Soll-Dauern.
 * Nur gesetzt, wenn die Moderationsleiste diesen Ablauf fahren soll — die
 * AR-Sitzung behält ohne diesen Block ihre bisherige Sechser-Kette.
 */
export interface StationsAblauf {
  /** [erste, letzte] Folie der Einführung (E1, E2) — 0-basiert, inklusiv. */
  einfuehrungSlides: [number, number];
  /** Folie „Die drei Felder erklärt" (Schritt Prompten). */
  promptenSlide: number;
  /** [erste, letzte] Folie der Generierung (Anstoß/Fortschritt). */
  generierungSlides: [number, number];
  /** [erste, letzte] Folie des Vortrags (V1–V3, T1–T3) — Auto-Flow. */
  vortragSlides: [number, number];
  /** Abschlussfolie A1 (Zähler). */
  abschlussSlide: number;
  /** Soll-Dauern in Sekunden — Konfiguration, nicht Code (Briefing 3). */
  phaseDauernSec: {
    einfuehrung: number;
    thema: number;
    prompten: number;
    arbeitsphase: number;
    generierung: number;
    vortrag: number;
    ergebnisse: number;
    abschluss: number;
  };
}

/** Ein Eingabefeld im Anforderungs-Formular. */
export interface RequirementFieldDef {
  /** Schlüssel in RequirementFields — muss dort existieren. */
  key:
    | "thema"
    | "was"
    | "fuerWen"
    | "problem"
    | "anforderungen"
    | "einschraenkungen"
    | "beispiel";
  label: string;
  placeholder: string;
  /** Mehrzeiliges Feld. Default: false. */
  multiline?: boolean;
  /** Wird mit dem Rahmenthema vorbelegt. Höchstens ein Feld pro Offsite. */
  prefillFromFraming?: boolean;
  /**
   * Dauerhaft sichtbarer Erklärtext unter dem Label — kein Tooltip. Für
   * Teilnehmer, die zum ersten Mal mit einer KI arbeiten, ist die Erklärung
   * Teil der Führung und darf nicht hinter einer Interaktion liegen.
   */
  hint?: string;
}

export interface OffsiteConfig {
  id: string;
  displayName: string;
  subdomain: string;
  /** Absolute Basis-URL der zugehörigen API (pro Offsite eigene Subdomain). */
  apiBaseUrl: string;
  deck: ComponentType<SlideProps>[];
  progressCuts: ProgressCut[];
  phases: PhaseId[];
  /** Verweise auf Prompt-Sätze in @offsite/shared — nie der Prompt-Text selbst. */
  ai: { evalPromptKey: string; overnightPromptKey: string };
  /**
   * 0-basierter Slide-Index, auf den beim Start der Overnight-Phase gesprungen
   * wird (Ankündigungs-Folie). Weglassen, wenn das Offsite keine solche Folie
   * hat — dann wird beim Phasenwechsel nicht gesprungen.
   */
  overnightAnnouncementSlide?: number;
  /**
   * 0-basierter Slide-Index für die Warte-Phase. Solange „Warten auf Gäste“
   * läuft, gehört auf die Leinwand, was die Gäste brauchen: Adresse, PIN,
   * QR-Code. Weglassen = kein Sprung, die Leinwand bleibt wo sie ist.
   */
  waitingSlide?: number;
  /**
   * Der Exkurs im Ablauf: Wohin der Moderator wechselt, während die KI baut.
   *
   * Steht in der Moderationsleiste zwischen Generierung und Ergebnissen und ist
   * ein Link — ein neuer Tab, damit die Sitzung im ersten stehen bleibt.
   * Weggelassen: kein Marker.
   */
  exkurs?: { label: string; url: string };
  /**
   * Anzahl der Folien, die frei über die Seitenleiste angefahren werden — die
   * Einführung. Alles danach führt die Moderationsleiste, damit Folie und
   * Ablauf-Schritt nicht auseinanderlaufen können.
   *
   * Ohne Angabe ist das ganze Deck frei navigierbar (Vertriebs-Verhalten).
   */
  introSlides?: number;
  /**
   * 0-basierter Slide-Index der Arbeitsphase. Sobald die Uhr läuft, gehört auf
   * die Leinwand die Zeit — nicht mehr der QR-Code. Ohne Angabe bleibt die
   * Warte-Folie stehen (bisheriges Verhalten).
   */
  workingSlide?: number;
  /**
   * 0-basierter Slide-Index der Themenwahl. Der Ablauf-Schritt „Thema" bringt
   * diese Folie auf die Leinwand — die Wahl fällt vor dem Gremium, nicht in
   * der Seitenleiste des Moderators.
   */
  themaSlide?: number;
  /**
   * Vorbereitete Rahmenthemen: ein Klick statt Tippen vor Publikum. Sie stehen
   * auf der Themen-Folie und im Moderatorpult — dieselbe Liste, damit beide
   * nicht auseinanderlaufen. Ein eigenes Thema lässt sich immer frei eingeben.
   */
  preparedTopics?: { short: string; full: string }[];
  /**
   * Text auf dem Teilnehmer-Handy, während die KI baut. Der Standard spricht
   * von „über Nacht“ — richtig fürs Offsite, falsch in einer Sitzung, in der
   * das Ergebnis zwei Minuten später auf der Leinwand steht.
   */
  overnightWaitLabel?: string;
  /**
   * Felder des Anforderungs-Formulars. Weggelassen = die sechs Vertriebs-Felder
   * (Rückwärtskompatibilität). Die AR-Session setzt hier drei.
   */
  requirementFields?: RequirementFieldDef[];
  /**
   * Ein Teilnehmer = ein Team. Überspringt Voting und Gruppierung; beim Login
   * wird automatisch ein eigenes Team vergeben.
   */
  soloTeams?: boolean;
  /**
   * Siezen statt duzen. Das Vertriebs-Offsite duzt (Kollegen unter sich), die
   * Gremien-Sitzung siezt (Aufsichtsrat). Betrifft nur die Teilnehmer-Ansicht;
   * Folien und Moderatorpult formulieren ihre Texte ohnehin selbst.
   */
  formal?: boolean;
  /**
   * Kurzzeichen in der Kopfzeile von Handy- und Admin-Ansicht. Ohne Angabe
   * "SVO · OFFSITE 2026" — in einer Gremien-Sitzung falsch, weil die Gäste
   * kein Offsite besuchen.
   */
  wordmark?: string;
  /**
   * Phasen, in denen die Leinwand die Workshop-Oberfläche zeigt statt der
   * Folien. Weggelassen = alle Workshop-Phasen (Vertriebs-Verhalten).
   *
   * Die Gremien-Sitzung setzt hier nur "reveal": In der Arbeitsphase zeigte
   * die Leinwand sonst die Moderator-Bedienung mit Dauer-Auswahl und einem
   * zweiten, konkurrierenden Timer — vor Publikum, geduzt, mit 15 Minuten
   * voreingestellt statt der acht des Ablaufs.
   */
  workshopOverlayPhases?: string[];
  copy?: Record<string, string>;
  /**
   * Login per Einmal-Code (6 Zeichen) statt 4-stelliger Gruppen-PIN. Jeder
   * Code funktioniert genau einmal und bindet das Ergebnis an eine Person.
   */
  codeLogin?: boolean;
  /**
   * Pflicht-Identitätsfelder nach dem Login, vor dem Editor. Nur zusammen
   * mit codeLogin sinnvoll — ohne Code gibt es keine Person, an der die
   * Angaben hängen.
   */
  identity?: { nameLabel: string; abteilungLabel: string };
  /**
   * Gestrippte Einzel-Oberfläche: keine Logout-Leiste, kein Team-Wording.
   * Für Sitzungen, in denen Einzelpersonen prompten und jedes Wort auf dem
   * Handy zählt.
   */
  einzelmodus?: boolean;
  /**
   * Anzahl der Bühnen-Plätze. Die Zugangsfolie zeigt damit „Bühne 3/6 ·
   * 12 aus dem Publikum" statt einer Gesamtzahl. Ohne Angabe bleibt die
   * alte Anzeige („4 sind angemeldet").
   */
  buehnenPlaetze?: number;
  /** Stations-Ablauf mit servergeführten Timern (Strategietag). */
  stationsAblauf?: StationsAblauf;
  /**
   * Sprechernotizen je Folien-Index — erscheinen klein in der Moderations-
   * leiste und im Pult, nie auf der Leinwand (Briefing Prompt-Teil 5).
   */
  sprechernotizen?: Record<number, string>;
}

export function assertOffsiteConfig(c: OffsiteConfig): void {
  if (!c.id) throw new Error("OffsiteConfig: id fehlt");
  if (!c.displayName) throw new Error("OffsiteConfig: displayName fehlt");
  if (!c.subdomain) throw new Error("OffsiteConfig: subdomain fehlt");
  if (!c.apiBaseUrl) throw new Error("OffsiteConfig: apiBaseUrl fehlt");
  if (!Array.isArray(c.deck) || c.deck.length === 0)
    throw new Error("OffsiteConfig: deck ist leer");
  if (!c.ai?.evalPromptKey || !c.ai?.overnightPromptKey)
    throw new Error("OffsiteConfig: ai-Prompt-Keys fehlen");
  if (c.requirementFields && c.requirementFields.length === 0)
    throw new Error("OffsiteConfig: requirementFields ist leer");
  if (
    c.requirementFields &&
    c.requirementFields.filter((f) => f.prefillFromFraming).length > 1
  )
    throw new Error(
      "OffsiteConfig: höchstens ein Feld darf prefillFromFraming setzen",
    );
  if (c.identity && !c.codeLogin)
    throw new Error("OffsiteConfig: identity erfordert codeLogin");
}
